import { Button, Radio, Typography } from "antd";
import { useContext, useEffect, useState } from "react";
import styled from "styled-components";
import { FluxoResposta } from "../cadastroCotaUsuario.types";
import { OnboardingFrame, OnboardingNavigationContext } from "../utils";

interface Props {
  fluxo: FluxoResposta;
  submitting: boolean;
  onConcluir: (payload: Record<string, unknown>) => Promise<void>;
}

const OPCOES_COTAS = [
  { quantidade: 2, valor: "R$ 225,80/ano" },
  { quantidade: 3, valor: "R$ 328,10/ano" },
  { quantidade: 4, valor: "R$ 427,70/ano" },
  { quantidade: 5, valor: "R$ 522,70/ano" },
  { quantidade: 6, valor: "R$ 605,80/ano" },
];

const PRECO_POR_QUANTIDADE: Record<number, string> = {
  1: "R$ 118,80",
  2: "R$ 225,80/ano",
  3: "R$ 328,10/ano",
  4: "R$ 427,70/ano",
  5: "R$ 522,70/ano",
  6: "R$ 605,80/ano",
};

const Conteudo = styled.div`
  display: grid;
  gap: 28px;
`;

const Inclusos = styled.div`
  display: grid;
  gap: 18px;

  .incluso-titulo {
    display: block;
    font-size: 16px;
    margin-bottom: 4px;
  }

  .incluso-descricao {
    color: #5f6b73;
    line-height: 1.55;
    margin: 0;
  }
`;

const BlocoComercial = styled.section`
  border: 1px solid #b9dadd;
  border-radius: 18px;
  background: #f4fbfb;
  padding: 24px;

  .quantidade,
  .equivalente,
  .renovacao,
  .observacao {
    display: block;
  }

  .quantidade {
    margin-top: 16px;
  }

  .preco {
    color: #003644;
    font-size: 32px;
    line-height: 1.2;
    margin: 6px 0;
  }

  .renovacao {
    margin-top: 16px;
  }

  .beneficios-plano {
    display: grid;
    gap: 8px;
    margin: 20px 0;
  }

  .observacao {
    color: #5f6b73;
    font-size: 13px;
    line-height: 1.5;
    margin-top: 16px;
  }
`;

const Upsell = styled.section`
  border-top: 1px solid #e1e8ea;
  padding-top: 28px;

  .opcoes-cotas {
    display: grid;
    gap: 12px;
    margin: 20px 0 16px;
  }

  .ant-radio-wrapper {
    align-items: center;
    border: 1px solid #d7e2e6;
    border-radius: 12px;
    margin: 0;
    padding: 13px 16px;
  }

  .ant-radio-wrapper-checked {
    background: #eef8f8;
    border-color: #0b6673;
  }
`;

const AcoesAlternativas = styled.div`
  display: grid;
  order: 1;
  justify-items: start;
  gap: 4px;

  .ant-btn {
    padding-inline: 0;
  }
`;

export function EtapaContratacao({ fluxo, submitting, onConcluir }: Props) {
  const { onVoltar } = useContext(OnboardingNavigationContext);
  const contratacaoSalva = fluxo.dadosPreenchidos?.contratacao as
    | { quantidadeCotas?: number }
    | undefined;
  const [mostrarUpsell, setMostrarUpsell] = useState(
    (contratacaoSalva?.quantidadeCotas ?? 1) > 1
  );
  const [quantidadeCotas, setQuantidadeCotas] = useState(
    contratacaoSalva?.quantidadeCotas ?? 1
  );

  useEffect(() => {
    const quantidadeSalva = contratacaoSalva?.quantidadeCotas ?? 1;
    setQuantidadeCotas(quantidadeSalva);
    setMostrarUpsell(quantidadeSalva > 1);
  }, [contratacaoSalva?.quantidadeCotas]);

  const precoAnual = PRECO_POR_QUANTIDADE[quantidadeCotas];

  return (
    <OnboardingFrame
      fluxo={fluxo}
      title="Ative seu anúncio de venda"
      description={
        <>
          Sua cota está pronta para ser anunciada. Ative o anúncio para divulgá-la na Multicotas, <strong>sem comissão ou corretagem sobre a venda</strong>.
        </>
      }
    >
      <Conteudo>
        <section>
          <Typography.Title level={3}>O que está incluído</Typography.Title>
          <Inclusos>
            <div>
              <Typography.Text strong className="incluso-titulo">📢 Anúncio ativo na Multicotas</Typography.Text>
              <Typography.Paragraph className="incluso-descricao">Sua cota fica disponível para pessoas interessadas em multipropriedade enquanto sua assinatura estiver ativa.</Typography.Paragraph>
            </div>
            <div>
              <Typography.Text strong className="incluso-titulo">💬 Contato com interessados e suporte</Typography.Text>
              <Typography.Paragraph className="incluso-descricao">Receba contatos de potenciais compradores e conte com o suporte da Multicotas durante o processo.</Typography.Paragraph>
            </div>
            <div>
              <Typography.Text strong className="incluso-titulo">💳 Possibilidade de financiamento para o comprador</Typography.Text>
              <Typography.Paragraph className="incluso-descricao">Interessados podem solicitar uma análise de financiamento com nossa parceira, ampliando as possibilidades de negociação.</Typography.Paragraph>
            </div>
            <div>
              <Typography.Text strong className="incluso-titulo">⚖️ Apoio jurídico no fechamento</Typography.Text>
              <Typography.Paragraph className="incluso-descricao">Se desejar, você poderá contar com escritório jurídico parceiro para auxiliar na transferência da cota.</Typography.Paragraph>
            </div>
          </Inclusos>
        </section>

        <BlocoComercial>
          <Typography.Title level={3} style={{ margin: 0 }}>Ativação do anúncio</Typography.Title>
          <Typography.Text strong className="quantidade">{quantidadeCotas} {quantidadeCotas === 1 ? "cota" : "cotas"}</Typography.Text>
          <Typography.Title level={1} className="preco">{precoAnual}</Typography.Title>
          {quantidadeCotas === 1 ? (
            <Typography.Text className="equivalente">equivalente a <strong>R$ 9,90/mês</strong></Typography.Text>
          ) : null}
          <Typography.Text strong className="renovacao">Cobrança anual · renovação automática</Typography.Text>
          <div className="beneficios-plano">
            <Typography.Text>✓ Sem comissão ou corretagem sobre a venda</Typography.Text>
            <Typography.Text>✓ Cancele a renovação quando quiser</Typography.Text>
          </div>
          <Button
            type="primary"
            size="large"
            loading={submitting}
            onClick={() => onConcluir({
              acao: "CONTRATAR_ASSINATURA",
              quantidadeCotas,
              formaPagamento: "CARTAO",
            })}
          >
            Ativar meu anúncio
          </Button>
          <div style={{ display: "none" }} aria-hidden="true">
            <Button
              type="text"
              disabled={submitting}
              onClick={() => onConcluir({ desejaVender: false })}
            >
              Agora não
            </Button>
            {onVoltar ? (
              <Button type="text" disabled={submitting} onClick={onVoltar}>
                ← Voltar
              </Button>
            ) : null}
          </div>
          <Typography.Text className="observacao">A assinatura é renovada automaticamente a cada 12 meses. Você pode cancelar a renovação antes da próxima cobrança.</Typography.Text>
        </BlocoComercial>

        <AcoesAlternativas>
          {onVoltar ? (
            <Button type="text" disabled={submitting} onClick={onVoltar}>
              ← Voltar
            </Button>
          ) : null}
        </AcoesAlternativas>

        <Upsell>
          <Typography.Title level={3}>Tem outras cotas que também gostaria de anunciar?</Typography.Title>
          <Typography.Paragraph type="secondary">Inclua mais cotas na mesma assinatura e aproveite valores reduzidos.</Typography.Paragraph>
          <Button onClick={() => setMostrarUpsell((aberto) => !aberto)}>
            Quero anunciar mais cotas
          </Button>

          {mostrarUpsell ? (
            <div>
              <Typography.Title level={3} style={{ marginTop: 28 }}>Quantas cotas você gostaria de anunciar?</Typography.Title>
              <Radio.Group className="opcoes-cotas" value={quantidadeCotas} onChange={(event) => setQuantidadeCotas(event.target.value)}>
                {OPCOES_COTAS.map((opcao) => (
                  <Radio key={opcao.quantidade} value={opcao.quantidade}>
                    <Typography.Text strong>{opcao.quantidade} cotas</Typography.Text> — {opcao.valor}
                  </Radio>
                ))}
              </Radio.Group>
              <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
                A cota que você está cadastrando será incluída na assinatura. As demais poderão ser cadastradas posteriormente.
              </Typography.Paragraph>
            </div>
          ) : null}
        </Upsell>
      </Conteudo>
    </OnboardingFrame>
  );
}
